import { useState, useCallback } from 'react';
import api from '@/lib/api';
import type { ExportFormat, ProcessImageRequest } from '../types';

interface UseImageExportOptions {
  exportToDataURL: (format: ExportFormat, quality: number) => string;
  exportToBlob: (format: ExportFormat, quality: number) => Promise<Blob | null>;
}

export function useImageExport({ exportToDataURL, exportToBlob }: UseImageExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /** Download image to local filesystem via browser */
  const exportToLocal = useCallback(
    async (fileName: string, format: ExportFormat = 'png', quality = 1) => {
      setIsExporting(true);
      setExportError(null);

      try {
        const blob = await exportToBlob(format, quality);
        if (!blob) throw new Error('Failed to export canvas');

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        setExportError(err instanceof Error ? err.message : 'Export failed');
      } finally {
        setIsExporting(false);
      }
    },
    [exportToBlob],
  );

  /** Send canvas data to server for Sharp processing and save to Fias */
  const exportToFias = useCallback(async (request: ProcessImageRequest) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const result = await api.processImage(request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Server export failed';
      setExportError(message);
      return null;
    } finally {
      setIsExporting(false);
    }
  }, []);

  /** Quick save canvas as PNG to Fias filesystem */
  const quickSave = useCallback(
    async (fileName: string) => {
      const dataUrl = exportToDataURL('png', 1);
      if (!dataUrl) return null;

      return exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: { format: 'png' },
        saveAs: { fileName },
      });
    },
    [exportToDataURL, exportToFias],
  );

  return {
    isExporting,
    exportError,
    exportToLocal,
    exportToFias,
    quickSave,
  };
}
